import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
} from '@mui/material';
import { apiService } from '../../services/api';

const CreateProjectDialog = ({ open, onClose, onProjectCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    domain: '',
  });
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadDomains();
    }
  }, [open]);

  const loadDomains = async () => {
    try {
      const data = await apiService.getDomains();
      setDomains(data);
    } catch (err) {
      console.error('Failed to load domains:', err);
      // Set default domains if API fails
      setDomains([
        { id: 'general', name: 'General' },
        { id: 'medical', name: 'Medical' },
        { id: 'legal', name: 'Legal' },
        { id: 'technical', name: 'Technical' },
        { id: 'history', name: 'Oral History' },
        { id: 'business', name: 'Business' },
        { id: 'academic', name: 'Academic' },
      ]);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newProject = await apiService.createProject({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        domain: formData.domain || null,
      });

      // Reset form
      setFormData({
        name: '',
        description: '',
        domain: '',
      });

      // Notify parent
      if (onProjectCreated) {
        onProjectCreated(newProject);
      }

      // Close dialog
      onClose();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create project';
      setError(errorMessage);
      console.error('Create project error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      description: '',
      domain: '',
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Project</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Project Name"
            value={formData.name}
            onChange={handleChange('name')}
            fullWidth
            required
            autoFocus
            placeholder="e.g., Interview with Dr. Smith"
            helperText="A descriptive name for your transcription project"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
            multiline
            rows={3}
            placeholder="Optional description of the project"
            helperText="Additional details about this project"
          />

          <FormControl fullWidth>
            <InputLabel>Domain (Optional)</InputLabel>
            <Select
              value={formData.domain}
              onChange={handleChange('domain')}
              label="Domain (Optional)"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {domains.map((domain) => (
                <MenuItem key={domain.id} value={domain.id}>
                  {domain.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name.trim()}
        >
          {loading ? 'Creating...' : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectDialog;
